<template>
  <div class="mop-image-wrapper" :class="{ 'p-invalid': hasError }">
    <Image
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :preview="preview"
      :imageClass="imageClass"
      :imageStyle="imageStyle"
      :previewButtonLabel="t(previewButtonLabel || 'Preview')"
      :zoomInLabel="t(zoomInLabel || 'Zoom In')"
      :zoomOutLabel="t(zoomOutLabel || 'Zoom Out')"
      :rotateRightLabel="t(rotateRightLabel || 'Rotate Right')"
      :rotateLeftLabel="t(rotateLeftLabel || 'Rotate Left')"
      :closeLabel="t(closeLabel || 'Close')"
      :downloadLabel="t(downloadLabel || 'Download')"
      v-bind="$attrs"
      @show="onShow"
      @hide="onHide"
      @error="onError"
    >
      <template v-if="$slots.preview" #preview>
        <slot name="preview" />
      </template>
    </Image>
    <small v-if="hasError" class="p-error">{{ fieldError }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Image from 'primevue/image'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { ImageProps, ImageEmits } from './types'

const props = withDefaults(defineProps<ImageProps>(), {
  alt: '',
  preview: true,
  previewButtonLabel: 'Preview',
  zoomInLabel: 'Zoom In',
  zoomOutLabel: 'Zoom Out',
  rotateRightLabel: 'Rotate Right',
  rotateLeftLabel: 'Rotate Left',
  closeLabel: 'Close',
  downloadLabel: 'Download'
})

const emit = defineEmits<ImageEmits>()

const { t } = useLocalization()
const { formState } = useFormContext()

// Form integration
const hasError = computed(() => {
  if (!formState || !props.name) return false
  return props.name in formState.errors.value
})

const fieldError = computed(() => {
  if (!formState || !props.name) return ''
  const errors = formState.errors.value[props.name]
  return errors ? errors[0] : ''
})

// Event handlers
const onShow = () => {
  emit('show')
}

const onHide = () => {
  emit('hide')
}

const onError = () => {
  emit('error')
  if (formState && props.name) {
    // Use setFieldValue to set an error state
    formState.setFieldValue(props.name, null)
    // Add validation with error message
    formState.addValidation(props.name, [{
      validator: () => false,
      message: t('Image failed to load')
    }])
    // Validate to show the error
    formState.validateField(props.name)
  }
}
</script>

<style lang="scss">
.mop-image-wrapper {
  display: inline-block;
  position: relative;

  .p-image {
    display: inline-block;
    position: relative;

    img {
      max-width: 100%;
      height: auto;
    }

    &.p-image-preview-container {
      .p-image-preview-indicator {
        background: rgba(0, 0, 0, 0.5);
        transition: background-color 0.2s;

        &:hover {
          background: rgba(0, 0, 0, 0.7);
        }
      }
    }

    .p-image-preview-toolbar {
      padding: 1rem;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 4px;

      button {
        margin: 0 0.5rem;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }

  &.p-invalid {
    .p-image {
      outline: 2px solid var(--red-500);
    }
  }
}
</style>
